import { SendIcon } from 'lucide-react'
import { useState } from 'react'

import { AutoResizeTextarea, Button } from '@/components/ui'
import { useComposerDraft } from '../hooks/useComposerDraft'
import type { PendingAttachments } from '../hooks/usePendingAttachments'
import { useTypingBeacon } from '../hooks/useTypingBeacon'
import type { SupportTicket } from '../types'
import {
  AttachmentPickerButton,
  PendingAttachmentList,
} from './PendingAttachments'

// A reply the user took back. A fresh object every time, so discarding the same
// text twice lands in the composer twice.
export interface RestoredReply {
  body: string
}

interface TicketComposerProps {
  ticket: SupportTicket
  attachments: PendingAttachments
  // The replies this thread is still holding. One of them already shows the
  // text of a send that failed, so a stored draft of the same words must not
  // come back here as a second copy.
  pendingBodies: string[]
  restoredReply: RestoredReply | null
  onSubmit: (body: string) => void
}

// The draft lives here so a keystroke re-renders the composer alone and leaves
// the transcript untouched.
export const TicketComposer = ({
  ticket,
  attachments,
  pendingBodies,
  restoredReply,
  onSubmit,
}: TicketComposerProps) => {
  const { notifyTyping, resetBeacon } = useTypingBeacon(ticket.publicId)
  const { draft, updateDraft, holdSentDraft, restoreDraft } = useComposerDraft(
    ticket.publicId,
    pendingBodies,
  )

  const [appliedReply, setAppliedReply] = useState(restoredReply)
  if (appliedReply !== restoredReply) {
    setAppliedReply(restoredReply)
    if (restoredReply) restoreDraft(restoredReply.body)
  }

  const submit = () => {
    const trimmed = draft.trim()
    if (!trimmed) return
    holdSentDraft(trimmed)
    onSubmit(trimmed)
    resetBeacon()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  // Pasted text still belongs to the textarea, so only a paste carrying files
  // is taken over.
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (event.clipboardData.files.length === 0) return
    event.preventDefault()
    attachments.addFiles(event.clipboardData.files)
  }

  return (
    <div className="shrink-0 border-t border-border bg-white px-4 py-3">
      {attachments.files.length > 0 && (
        <div className="mb-2">
          <PendingAttachmentList
            files={attachments.files}
            onRemove={attachments.removeFile}
          />
        </div>
      )}

      {ticket.status === 'resolved' && (
        <p className="mb-2 text-xs text-ink-subtle">
          This ticket is resolved. Replying reopens it.
        </p>
      )}

      <div className="flex items-end gap-2">
        <AttachmentPickerButton onPick={attachments.addFiles} />
        <AutoResizeTextarea
          value={draft}
          onChange={(event) => {
            updateDraft(event.target.value)
            notifyTyping()
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="Write a reply…"
          maxRows={6}
          className="min-h-9 flex-1 py-1.5 leading-5 placeholder:text-ink-subtle"
          aria-label="Reply"
        />
        <Button
          isIcon
          aria-label="Send reply"
          onClick={submit}
          disabled={!draft.trim()}
          className="bg-brand-deep hover:bg-brand-deep/90 disabled:bg-blue-2 disabled:text-blue-4"
        >
          <SendIcon />
        </Button>
      </div>
    </div>
  )
}
